(ns app.front.view.committees.common
  (:require [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.util :as futil]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [reagent.core :as r]))

(defn add-widget [{:keys [state on-start-add]} child]
  (let [tr (<sub [:tr])]
    (case state
      :in-progress (cc/loading-spinner {:with-overlay false})
      :visible child
      (when on-start-add
        (cc/button {:on-click         on-start-add
                    :icon             i/plus
                    :icon-description ""}
                   (tr [:add]))))))

(def edit-entity-container
  :div.bg-ui-violet-10.mb-4.border-solid.border-l-8.border-ui-violet.flex.flex-col.md:flex-row.items-start)

(defn edit-controls [{:keys [name-attr desc-attr *params]}]
  (let [tr     (<sub [:tr])
        params @*params]
    [:<>
     [ui-i18n/localized-string-edit (util/mmap {:label-text          (tr [name-attr])
                                                :data-cy             (name name-attr)
                                                :required            true
                                                :strings             (get params name-attr)
                                                :allowed-locales-sub ::gas/allowed-locales
                                                :on-change           (fn [vals]
                                                                       (swap! *params assoc name-attr vals))})]
     [ui-i18n/localized-string-edit {:label-text          (tr [desc-attr])
                                     :id                  "description"
                                     :data-cy             (name desc-attr)
                                     :class               "mt-4"
                                     :strings             (get params desc-attr)
                                     :allowed-locales-sub ::gas/allowed-locales
                                     :multiline?          true
                                     :on-change           (fn [vals]
                                                            (swap! *params assoc desc-attr vals))}]]))

(defn action-buttons [{:keys [on-cancel on-ok disabled? ok-text cancel-kind ok-icon]}]
  (let [tr (<sub [:tr])]
    [:div.flex.flex-row.justify-end.self-end.md:self-start
     (cc/button {:on-click         on-cancel
                 :kind             (or cancel-kind :tertiary)
                 :icon             i/remove
                 :icon-description ""}
                (tr [:cancel]))
     (cc/button {:on-click         on-ok
                 :icon             ok-icon
                 :icon-description ""
                 :disabled         disabled?
                 :class-name       "ml-2"}
                ok-text)]))

(defn add-entity-widget [{:keys [on-create on-cancel title name-attr desc-attr]}]
  (r/with-let [*params (r/atom {})]
    (let [tr        (<sub [:tr])
          disabled? (-> @*params name-attr str/blank?)]
      [:div.mb-4
       [ui-add/add-widget
        {:title   title
         :buttons [{:icon     i/remove
                    :text     (tr [:cancel])
                    :on-click on-cancel}
                   {:icon      i/plus
                    :disabled? disabled?
                    :text      (tr [:committee/create])
                    :on-click  #(on-create @*params)}]}
        [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]
        [edit-controls (util/mmap name-attr desc-attr *params)]]])))

(defn empty-text [text]
  [:div.text-xl.text-gray-500.pb-4 text])

(defn item-editor [{:keys [item on-save on-cancel name-attr desc-attr name-fn desc-fn]}]
  (r/with-let [*params (-> item
                           (select-keys [:db/id name-attr desc-attr])
                           (p/update-if-contains name-attr i18n-model/strings-map)
                           (p/update-if-contains desc-attr i18n-model/strings-map)
                           r/atom)]
    (let [tr        (<sub [:tr])
          params    @*params
          disabled? (-> params name-attr str/blank?)]
      [edit-entity-container
       [:div.flex-1>div.py-6.px-8
        [edit-controls (util/mmap name-attr desc-attr name-fn desc-fn *params)]]
       [:div.gap-4.flex.flex-col.justify-end.items-end
        [action-buttons (util/mmap {:on-ok       #(on-save params)
                                    :cancel-kind :ghost
                                    :ok-icon     i/check
                                    :ok-text     (tr [:save])}
                                   on-cancel disabled?)]
        [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]]])))

(def item-container
  :div.bg-gray-100.mb-4.border-solid.border-l-8.border-gray-300.flex.flex-col.md:flex-row.items-start)

(defn list-item-view [{:keys [item on-edit
                              on-delete
                              active?
                              name-fn
                              desc-fn
                              delete-title
                              delete-confirm-content
                              on-retract
                              retractable?
                              retract-title
                              retract-confirm-content
                              retract-not-allowed-info]}]
  (let [id   (:db/id item)
        tr   (<sub [:tr])
        lang (<sub [:language])
        name (name-fn item lang)
        desc (desc-fn item lang)]
    [item-container
     {:class (when active? "border-ui-green")}
     [:div.flex-1
      [:div.flex.flex-col.md:flex-row.md:items-center
       [:div.p-4.pl-8.flex.flex-row.items-center
        [:h4 name]
        [:span.ml-4.uppercase.text-gray-600.flex.flex-row.items-center
         {:class (when active? "text-ui-green")}
         (cc/icon {:fill "currentcolor" :icon (if active? i/check i/remove) :description "" :class-name "mr-2" :aria-hidden true})
         (tr [(:domain/state item)])]]]
      [:div.pb-6.px-8.leading-normal.whitespace-pre-wrap desc]]
     (when active?
       [:div.flex.flex-row.self-end.md:self-start
        (cc/button {:on-click         on-edit
                    :icon             i/pen
                    :icon-description ""
                    :kind             :ghost}
                   (tr [:edit]))
        [ui/modal-confirmation-button
         {:tr                  tr
          :id                  (str "delete-" id)
          :modal-title         delete-title
          :trigger-button-opts {:icon             i/trash
                                :kind             :ghost
                                :icon-description ""}
          :trigger-button-text (tr [:delete])
          :no-button-text      (tr [:no])
          :yes-button-text     (tr [:yes])
          :on-yes-click        on-delete}
         [:div delete-confirm-content]
         [:div.mt-2.font-bold name]]])
     (when-not active?
       [:div.flex.flex-row.self-end.md:self-start.items-center
        (when-not retractable?
          [:span.italic.text-right.text-xs retract-not-allowed-info])
        [ui/modal-confirmation-button
         {:tr                  tr
          :id                  (str "retract-" id)
          :modal-title         retract-title
          :trigger-button-opts {:icon             i/trash
                                :kind             :ghost
                                :icon-description ""}
          :trigger-button-text (tr [:committee/retract])
          :no-button-text      (tr [:no])
          :yes-button-text     (tr [:yes])
          :on-yes-click        on-retract
          :disabled?           (not retractable?)}
         [:div retract-confirm-content]
         [:div.mt-2.font-bold name]]])]))

(defn list-item [{:keys [item status on-save on-cancel on-edit on-delete on-retract name-attr desc-attr name-fn desc-fn
                         active? delete-title delete-confirm-content retractable? retract-title retract-confirm-content
                         retract-not-allowed-info]}]
  (case status
    :editing
    [item-editor (util/mmap item on-save on-cancel name-attr desc-attr name-fn desc-fn)]
    :in-progress
    [:div.bg-gray-100.mb-4.border-solid.border-l-8.border-gray-300.flex.flex-col.md:flex-row.items-start
     (cc/loading-spinner {:with-overlay false :class-name "m-2"})]
    [list-item-view (util/mmap on-edit on-delete item name-fn desc-fn active? delete-title delete-confirm-content
                               on-retract retractable? retract-title retract-confirm-content retract-not-allowed-info)]))

(def item-status->icon
  {:committee-item.state/done      i/check
   :committee-item.state/postponed i/clock
   :committee-item.state/canceled  i/remove})

(defn templates-section [{:keys [tr agenda-template packet-template]}]
  [:div.mb-6
   [:div.grid.grid-cols-2.gap-2.text-sm
    [:div
     [:div (tr [:committee/agenda-pdf-template])]
     [:div.mt-2.text-gray-600 (or (-> agenda-template first :label) "-")]]
    [:div
     [:div (tr [:meeting/packet-template])]
     [:div.mt-2.text-gray-600 (or (-> packet-template first :label) "-")]]]])

(defn scheduling-rules-section [{:keys [tr start-day repeat-count frequency]}]
  (let [start-day-label (when (some? start-day)
                          (futil/tr-weekday tr (str (mod (inc start-day) 7))))
        frequency-label (when frequency
                          (futil/tr-date-frequency tr (name frequency)))]
    [:div.mb-6
     [:h2.mb-3.text-lg.font-bold (tr [:meeting/scheduling-rules])]
     [:div.flex.gap-12.text-sm
      [:div.mr-6
       [:div (tr [:meeting/start-on])]
       [:div.mt-2.text-gray-600 (or start-day-label "-")]]
      [:div
       [:div (tr [:meeting/repeat-every])]
       [:div.mt-2.text-gray-600
        (if repeat-count
          (str repeat-count " "
               (or frequency-label
                   (futil/tr-date-frequency tr "week")))
          "-")]]]]))
